import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { paths } from "@/lib/api-types";
import { adminQueryKeys } from "./queryKeys";
import {
  createListQueryInit,
  createDetailQueryInit,
  listQueryOptions,
  detailQueryOptions,
} from "./hooks";
import type { SortOrder } from "./sort";

export type AdminParticipantListItem =
  paths["/api/admin/participants"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

type AdminParticipantsListQuery = {
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortOrder: SortOrder;
  filter?: string;
};

export function useAdminParticipantsList(
  params: AdminParticipantsListQuery,
  options?: { enabled?: boolean },
) {
  const init = createListQueryInit(params, "createdAt");
  return apiClient.useQuery(
    "get",
    "/api/admin/participants",
    init,
    {
      ...listQueryOptions,
      enabled: options?.enabled ?? true,
    },
  );
}

export type AdminParticipantDetail =
  paths["/api/admin/participants/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["participant"];

export function useAdminParticipant(participantId: string | undefined) {
  const init = createDetailQueryInit(participantId);
  return apiClient.useQuery(
    "get",
    "/api/admin/participants/{id}",
    init,
    detailQueryOptions(!!participantId),
  );
}

export function useAdminParticipantApprove() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/participants/{id}/approve", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.detail,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.checkouts.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.checkouts.detail,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.events.participantStats,
      });
    },
  });
}

export function useAdminParticipantReject() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/participants/{id}/reject", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.detail,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.checkouts.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.checkouts.detail,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.events.participantStats,
      });
    },
  });
}

export function useAdminParticipantCancel() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/participants/{id}/cancel", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.detail,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.checkouts.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.checkouts.detail,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.events.participantStats,
      });
    },
  });
}

export function useAdminParticipantTakeBalance() {
  const queryClient = useQueryClient();
  return apiClient.useMutation(
    "post",
    "/api/admin/participants/{id}/take-balance",
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.participants.list,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.participants.detail,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.list,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.detail,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.events.participantStats,
        });
      },
    },
  );
}

export function useAdminParticipantUpdate() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("patch", "/api/admin/participants/{id}", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.detail,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.events.participantGroups,
      });
    },
  });
}
